import { QueryClient } from '@tanstack/react-query';
import chatNotificationMp3 from 'Assets/audio/MessageNotification.mp3';
import videoEndedNotificationMp3 from 'Assets/audio/VideoEndedNotification.mp3';
import videoJoinNotificationMp3 from 'Assets/audio/VideoJoinNotification.mp3';
import videoLeaveNotificationMp3 from 'Assets/audio/VideoLeaveNotification.mp3';
import videoStartNotificationMp3 from 'Assets/audio/VideoStartNotification.mp3';
import type { CallLogType } from 'Components/CallLogs/types';
import {
  API_ENDPOINTS,
  NODE_ENV_LOCAL_OR_DEVELOPMENT,
  NODE_ENV_PRODUCTION,
  PRESENCE_IDLE_TIME,
  PUSHER_CHANNEL_EXTENSION,
} from 'Constants/env';
import { FORCE_POLLING } from 'Constants/featureFlags';
import { SELECTED_RING_DEVICE, SELECTED_SPEAKER } from 'Constants/localstorage';
import { WN_MESSAGE_CREATED_PREFIX } from 'Constants/webNotifications';
import { getUnixTime } from 'date-fns';

import { QUERY_KEYS as BADGE_COUNTERS_QUERY_KEYS } from 'Hooks/badgeCounters';
import { ConfigurationResponse, ParticipantSummary } from 'Interfaces/apiDtos';
import { AxiosResponseT } from 'Interfaces/axiosResponse';
import { PersonHistoryResponse } from 'Interfaces/calls';
import {
  IConversationNotification,
  IConversationUpdatedNotification,
  IConversationViewStateUpdated,
  IFavoritesMutedMentionNotification,
  IMessageUpdatedNotification,
  IParticipantsUpdatedNotification,
  IPinnedMessageNotification,
  IPresenceUpdated,
  ISharedItemCreatedNotification,
  ISharedItemRemovedNotification,
} from 'Interfaces/notifications';
import { IPreferences } from 'Interfaces/preferences';
import { VoicemailBadgeCounter } from 'Interfaces/voicemails';
import localforage from 'localforage';
import { get, isEmpty } from 'lodash';
import {
  action,
  makeObservable,
  observable,
  reaction,
  runInAction,
  when,
  computed,
} from 'mobx';
// import { fromPromise } from 'mobx-utils';
import { ContactModelV2, IPusherContact } from 'Models/ContactModelV2';
import { ConversationModel, MessageModel, PresenceModel } from 'Models/index';
import { IPinnedMessages } from 'Models/PinnedMessageModel';
import MessageStatusModel, { IMessageStatusModel } from 'Models/StatusModel';
import { QUERY_KEY_FETCH_CALL_LOGS } from 'Modules/callLogs/index.request';
import {
  fetchConversation,
  QUERY_KEY_FETCH_CONVERSATION,
} from 'Modules/conversation/conversations-list/requests/index.requests';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { fetchMessage } from 'Modules/message/index.requests';
import { useNotificationStore } from 'Modules/notification/store';
import {
  removeSharedItemFromQueryCache,
  upsertSharedItemInQueryCache,
} from 'Modules/sharedItems/requests/index.requests';
import { mapSharedItemCreatedToSharedItem } from 'Modules/sharedItems/utils';
import { CallManager } from 'Modules/softphone/services/CallManager';
import { SoftphoneCoordinator } from 'Modules/softphone/utils/softphoneCoordinator';
import moment from 'moment-timezone';
import Pusher, { Channel, Options } from 'pusher-js/with-encryption';
import { BaseStore } from 'Stores/BaseStore';
import { RootStore } from 'Stores/RootStore';
import { isNullOrUndefined } from 'util';
import { getCurrentConversationId } from 'Utils/getCurrentConversationId';
import { sendIpcNewMessage } from 'Utils/ipcRendererEvents';
import { getLogger, Logger } from 'Utils/logger';
import { bugsnagClient } from 'Utils/logUtils';
import { normalizeGifPayload } from 'Utils/normalizeGifPayload';
import { logError } from 'Utils/phoneLogUtils';
import { updatePusherLogs } from 'Utils/pusherLogsUtil';
import { getISOStringFromTimeUUID } from 'Utils/timeUUIDParser';
import { getBearerAuthToken } from '~/api';
import { ForcePollingFeatureFlags } from '../providers/PollingProvider/index.types';
import { PusherConnectionRecoveryService } from '../services/pusherConnectionRecoveryService';

// tslint:disable-next-line:no-reference
/// <reference path="../../../typings/global.d.ts"/>

/** Last-resort "OneOnOne" notification title, used instead of the raw `topic`. */
const ONE_ON_ONE_FALLBACK_TITLE = 'New message';

/**
 * Builds a person's full name, or `null` when no part of it is known.
 * Interpolating the fields directly yields "undefined undefined" (CUU2-3526).
 */
const formatPersonFullName = (person?: {
  firstName?: string;
  lastName?: string;
  name?: string;
}): string | null => {
  if (!person) {
    return null;
  }
  const fullName = [person.firstName, person.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || person.name?.trim() || null;
};

export class PusherStore extends BaseStore {
  #queryClient: QueryClient;

  private logger: Logger;
  private recoveryService!: PusherConnectionRecoveryService;
  private authRetryTimeoutId: number | null = null;
  private reconnectRecoveryTimeoutId: number | null = null;
  private offlineDelayTimeoutId: number | null = null;
  /** Timestamp (ms) when the first `connecting` event fired in the current
   *  disconnect episode. Persists across rapid connecting→connected cycles
   *  so we can measure total time spent disconnected, not per-cycle. */
  private disconnectedSince: number | null = null;
  private previouslyBothActuallySubscribed = false;

  constructor(rootStore: RootStore, queryClient: QueryClient) {
    super(rootStore);
    this.#queryClient = queryClient;
    makeObservable(this);
    this.logger = getLogger();
    this.#queryClient = queryClient;

    // Initialize Pusher recovery service
    this.recoveryService = new PusherConnectionRecoveryService(rootStore, {
      initialDelay: 1000,
      maxDelay: 16000,
      maxAttempts: 10,
      enableConsoleLogging: NODE_ENV_LOCAL_OR_DEVELOPMENT,
    });
  }

  clearAllData = () => {
    if (this.personalChannel !== null && this.personalChannel.subscribed) {
      this.personalChannel.unbind_all();
      this.personalChannel = null;
    }
    if (this.accountChannel !== null && this.accountChannel.subscribed) {
      this.accountChannel.unbind_all();
      this.accountChannel = null;
    }
    // Clear any pending auth retry timeout
    if (this.authRetryTimeoutId !== null) {
      window.clearTimeout(this.authRetryTimeoutId);
      this.authRetryTimeoutId = null;
    }
    // Clear pending offline delay and disconnect tracking
    if (this.offlineDelayTimeoutId !== null) {
      window.clearTimeout(this.offlineDelayTimeoutId);
      this.offlineDelayTimeoutId = null;
    }
    this.disconnectedSince = null;
    // Don't cancel auth retry if recovery is currently in progress
    // (this prevents the recovery service from cancelling itself)
    if (!this.recoveryService.isRecovering) {
      this.cancelAuthRetry();
    } else {
      console.info(
        'PusherStore: Skipping cancelAuthRetry - recovery in progress'
      );
    }
    this.setPersonalChannelSubscribed(false);
    this.setAccountChannelSubscribed(false);
    if (this.socket !== null && this.socket.connection) {
      this.socket.disconnect();
      this.socket = null;
    }
  };

  @observable
  isOnline = true;

  @action
  setOnline = (isOnline: boolean) => {
    this.isOnline = isOnline;
  };

  @observable
  isPersonalChannelSubscribed = false;

  @action
  setPersonalChannelSubscribed = (isPersonalChannelSubscribed: boolean) =>
    (this.isPersonalChannelSubscribed = isPersonalChannelSubscribed);

  @observable
  isAccountChannelSubscribed = false;

  @action
  setAccountChannelSubscribed = (isAccountChannelSubscribed: boolean) =>
    (this.isAccountChannelSubscribed = isAccountChannelSubscribed);

  @observable
  lastVoicemailId = null;

  @action
  setLastVoicemailId = (id: number) => (this.lastVoicemailId = id);

  private async scheduleAuthRetry(): Promise<void> {
    if (this.recoveryService.isRecovering || !navigator.onLine) {
      return;
    }

    await this.recoveryService.startAuthRecovery();
  }

  /**
   * Cancels any ongoing auth recovery
   */
  private cancelAuthRetry(): void {
    // Clear any pending auth retry timeout
    if (this.authRetryTimeoutId !== null) {
      window.clearTimeout(this.authRetryTimeoutId);
      this.authRetryTimeoutId = null;
    }
    this.recoveryService.stopRecovery();
  }

  private updatePreviousSubscriptionsState(): void {
    this.previouslyBothActuallySubscribed =
      this.isPersonalChannelActuallySubscribed &&
      this.isAccountChannelActuallySubscribed;
  }

  private cancelAuthRetryIfTransitionedFromBothFalseToBothTrue(): void {
    const bothNow =
      this.isPersonalChannelActuallySubscribed &&
      this.isAccountChannelActuallySubscribed;
    if (bothNow && !this.previouslyBothActuallySubscribed) {
      this.cancelAuthRetry();
    }
    this.previouslyBothActuallySubscribed = bothNow;
  }

  /**
   * Recovery service state for UI components
   */
  @computed
  get recoveryState() {
    return {
      isRecovering: this.recoveryService?.isRecovering ?? false,
      attemptCount: this.recoveryService?.recoveryAttemptCount ?? 0,
      lastAttempt: this.recoveryService?.lastRecoveryAttempt ?? null,
    };
  }

  /**
   * Checks if the underlying Pusher socket connection is established
   * This is different from isOnline which also requires channel authentication
   */
  @computed
  get isSocketConnected(): boolean {
    return this.socket?.connection?.state === 'connected';
  }

  waitUntilPusherConnected = async () => {
    try {
      await when(
        () => {
          return this.isOnline;
        },
        { timeout: 10000 }
      );
    } catch (error) {
      logError(
        error,
        "Pusher didn't connect",
        'PusherStore',
        'waitUntilPusherConnected',
        'error'
      );
    }
  };

  waitUntilPersonalChannelSubscribed = async () => {
    try {
      await when(
        () => {
          return this.isPersonalChannelSubscribed;
        },
        { timeout: 10000 }
      );
    } catch (error) {
      logError(
        error,
        "Pusher didn't subscribe to personal channel",
        'PusherStore',
        'waitUntilPusherConnected',
        'error'
      );
    }
  };

  /**
   * If the user is currently viewing a conversation and the window is focused,
   * tell the server we've read up to the newest message and reset the local
   * unread counter to 0. This prevents the badge from inflating with messages
   * the user has already seen after a reconnect recovery.
   */
  private markActiveConversationAsRead = () => {
    const { uiStore, messageStore, participantStore } = this.rootStore;
    const activeConvId = getCurrentConversationId();
    if (!activeConvId || !uiStore.IsFocused) return;

    const grpMsgs =
      messageStore.selectGroupedMessagesForConversation(activeConvId);
    if (!grpMsgs?.NewestMessageId) return;

    void participantStore
      .updateMyLastReadMessage(activeConvId, grpMsgs.NewestMessageId)
      .then(() => {
        void uiStore.setConversationAndTotalUnreadCount(
          activeConvId,
          0,
          null,
          0
        );
      });
  };

  @action
  initialPusher = (config: ConfigurationResponse) => {
    if (!config?.pusher?.appKey) {
      throw new Error('Missing required Pusher configuration: appKey');
    }

    const preferPusherXHRPolling =
      config.feature?.preferPusherXHRPolling?.enabled;

    const authToken = getBearerAuthToken();

    if (!authToken) {
      throw new Error('Missing authorization token for Pusher');
    }

    this.socket = new Pusher(config.pusher.appKey, {
      cluster: 'mt1',
      channelAuthorization: {
        transport: 'ajax',
        endpoint: API_ENDPOINTS.PusherAuth,
        headersProvider: () => ({
          Authorization: getBearerAuthToken(),
        }),
      },
      // Conditionally prefer XHR polling over websockets to handle network conditions
      // where websockets may face connectivity issues or be blocked
      ...(preferPusherXHRPolling && {
        disabledTransports: ['ws', 'wss'],
      }),
      activityTimeout: 5000,
      pongTimeout: 1000,
    } satisfies Options);
  };

  initSubscriptions = (config: ConfigurationResponse) => {
    this.initialPusher(config);

    const shouldEnablePollingLogs = (
      this.rootStore.personStore.ldClient?.variation(
        FORCE_POLLING,
        false
      ) as ForcePollingFeatureFlags
    )?.pusherLogs;

    try {
      if (shouldEnablePollingLogs) {
        console.log('Setting up global pusher event listener');
        const SENSITIVE_EVENTS = ['MessageCreated', 'MessageUpdated'];
        const IGNORED_EVENTS = ['pusher:pong', 'pusher:ping'];
        const parsePayload = (value: any) => {
          if (typeof value !== 'string') return value;
          try {
            return JSON.parse(value);
          } catch (e) {
            return value;
          }
        };
        this.socket.bind_global((eventName: string, data: any) => {
          if (IGNORED_EVENTS.includes(eventName)) return;
          const message = `Pusher logs: global event;`;
          const payload = parsePayload(data);
          const redactedData =
            payload &&
            typeof payload === 'object' &&
            SENSITIVE_EVENTS.includes(eventName)
              ? {
                  conversationId: payload.conversationId,
                  messageId: payload.messageId,
                }
              : payload;

          const eventData = {
            eventName,
            data: redactedData,
          };
          console.log(message, eventData);
          this.logger.info(message, eventData);
        });

        this.socket.connection.bind('error', (error: Error) => {
          const message = `Pusher logs: error;`;
          const data = {
            error,
          };
          this.logger.error(message, data);
        });
      }
    } catch (error) {
      console.log('error', error);
    }

    // https://pusher.com/docs/client_api_guide/client_connect#connection-status-events
    this.socket.connection.bind('state_change', async (states) => {
      console.info(
        `Pusher state_change from ${states.previous} to ${states.current}`
      );

      // Logging for debugging
      const shouldEnablePollingLogs = (
        this.rootStore.personStore.ldClient?.variation(
          FORCE_POLLING,
          false
        ) as ForcePollingFeatureFlags
      )?.pusherLogs;

      if (shouldEnablePollingLogs) {
        const message = `Pusher logs: state_change;`;
        const data = {
          previous: states.previous,
          current: states.current,
        };
        this.logger.info(message, data);

        await updatePusherLogs({
          type: 'pusher_state_change',
          previous: states.previous,
          current: states.current,
        });
      }

      if (states.current === 'unavailable' || !navigator.onLine) {
        // Immediately go offline for hard disconnects
        if (this.offlineDelayTimeoutId !== null) {
          window.clearTimeout(this.offlineDelayTimeoutId);
          this.offlineDelayTimeoutId = null;
        }
        this.disconnectedSince = this.disconnectedSince ?? Date.now();
        this.setOnline(false);
        this.setPersonalChannelSubscribed(false);
        this.setAccountChannelSubscribed(false);
        this.updatePreviousSubscriptionsState();
      } else if (states.current === 'connecting') {
        // Record when the disconnect episode started. This timestamp
        // persists across rapid connecting→connected cycles so we
        // measure total disconnected time, not per-cycle.
        if (this.disconnectedSince === null) {
          this.disconnectedSince = Date.now();
        }

        // Schedule a delayed offline mark. If Pusher reconnects within
        // 3 s the timer is cancelled, but disconnectedSince stays so
        // we still know we were disconnected.
        if (this.offlineDelayTimeoutId === null) {
          this.offlineDelayTimeoutId = window.setTimeout(() => {
            this.offlineDelayTimeoutId = null;
            this.setOnline(false);
            this.setPersonalChannelSubscribed(false);
            this.setAccountChannelSubscribed(false);
            this.updatePreviousSubscriptionsState();
          }, 3000);
        }
      }

      // Handle reconnection
      if (
        states.previous !== 'connected' &&
        states.current === 'connected' &&
        navigator.onLine
      ) {
        // Cancel pending offline-delay timer — we're connected now
        if (this.offlineDelayTimeoutId !== null) {
          window.clearTimeout(this.offlineDelayTimeoutId);
          this.offlineDelayTimeoutId = null;
        }

        // Determine whether recovery is needed. Two conditions:
        // 1. isOnline is false (the 3 s timer already fired), OR
        // 2. We've been in a disconnect episode for ≥ 3 s total
        //    (rapid connecting→connected cycles kept cancelling the
        //    timer, but the cumulative time warrants recovery).
        const disconnectedMs = this.disconnectedSince
          ? Date.now() - this.disconnectedSince
          : 0;
        const needsRecovery = !this.isOnline || disconnectedMs >= 3000;

        if (needsRecovery) {
          // Clear the disconnect episode
          this.disconnectedSince = null;
          this.setOnline(true);

          // Connection is back, channels will resubscribe
          this.cancelAuthRetryIfTransitionedFromBothFalseToBothTrue();
          // If channels fail to subscribe after reconnect, schedule retry
          if (this.authRetryTimeoutId !== null) {
            window.clearTimeout(this.authRetryTimeoutId);
            this.authRetryTimeoutId = null;
          }
          this.authRetryTimeoutId = window.setTimeout(() => {
            if (
              (!this.isPersonalChannelActuallySubscribed ||
                !this.isAccountChannelActuallySubscribed) &&
              !this.recoveryService.isRecovering
            ) {
              void this.scheduleAuthRetry();
            }
            this.authRetryTimeoutId = null;
          }, 1000);

          // Dismiss network error banner immediately (UI feedback).
          this.rootStore.notificationStore.setIsNetworkErrorDisplayed(false);

          // Debounce recovery: collapse rapid cycles into a single
          // recovery pass 2 s after the last connected event.
          if (this.reconnectRecoveryTimeoutId !== null) {
            window.clearTimeout(this.reconnectRecoveryTimeoutId);
            this.reconnectRecoveryTimeoutId = null;
          }
          this.reconnectRecoveryTimeoutId = window.setTimeout(() => {
            this.reconnectRecoveryTimeoutId = null;

            // Refresh user session and data
            this.rootStore.personStore.tryRefreshLocalLogin();

            // Refresh voicemail badges
            void this.#queryClient.invalidateQueries({
              queryKey: BADGE_COUNTERS_QUERY_KEYS.voicemails,
            });

            // Reload conversations for fresh unread counts, then mark the
            // active conversation as read so the badge doesn't inflate with
            // messages the user has already seen on screen.
            void this.rootStore.conversationStore
              .loadConversationsGet()
              .then(() => {
                this.markActiveConversationAsRead();
              });
          }, 2000);
        } else {
          // Quick blip (< 3 s total) — no recovery needed, clear episode
          this.disconnectedSince = null;
        }
      }
    });

    const pusherChannelExtension = PUSHER_CHANNEL_EXTENSION
      ? `${PUSHER_CHANNEL_EXTENSION}-`
      : '';
    this.personalChannel = this.socket.subscribe(
      `private-${pusherChannelExtension}a=${this.rootStore.personStore.loggedInAccountId}-p=${this.rootStore.personStore.loggedInPersonId}`
    );

    this.accountChannel = this.socket.subscribe(
      `private-${pusherChannelExtension}a=${this.rootStore.personStore.loggedInAccountId}`
    );

    this.accountChannel.bind('pusher:subscription_succeeded', () => {
      this.setAccountChannelSubscribed(true);
      this.cancelAuthRetryIfTransitionedFromBothFalseToBothTrue();
      const message = `Pusher logs: account channel subscription succeeded;`;
      const data = {
        channel: this.accountChannel.name,
      };
      this.logger.info(message, data);
    });

    this.personalChannel.bind('pusher:subscription_succeeded', () => {
      this.setPersonalChannelSubscribed(true);
      this.cancelAuthRetryIfTransitionedFromBothFalseToBothTrue();
      const message = `Pusher logs: personal channel subscription succeeded;`;
      const data = {
        channel: this.personalChannel.name,
      };
      this.logger.info(message, data);
    });

    this.personalChannel.bind('pusher:subscription_error', (error) => {
      const status = error?.status;
      const message = `Pusher logs: personal channel subscription error;`;
      const data = {
        error,
        status,
        channel: this.personalChannel.name,
      };
      // eslint-disable-next-line no-console
      console.error(message, data);
      this.logger.error(message, data);

      if (status === 401) {
        this.logger.warn('Pusher auth failed with 401', {
          channel: 'personal',
          status,
        });
      }

      void this.scheduleAuthRetry();
    });

    this.accountChannel.bind('pusher:subscription_error', (error) => {
      const status = error?.status;
      const message = `Pusher logs: account channel subscription error;`;
      const data = {
        error,
        status,
        channel: this.accountChannel.name,
      };
      // eslint-disable-next-line no-console
      console.error(message, data);
      this.logger.error(message, data);

      if (status === 401) {
        this.logger.warn('Pusher auth failed with 401', {
          channel: 'account',
          status,
        });
      }

      void this.scheduleAuthRetry();
    });

    /** -- Contacts API -- */
    this.accountChannel.bind('ContactCreated', (data: string) => {
      const contact = this.createContact(data);

      // For Bhive contacts, we already receive the ContactUpdated event on creation, so no need to handle it here too.
      if (contact.source === 'Bhive') {
        return;
      }

      void this.rootStore.personStore.handleUpdateContactSuccess(contact);
    });

    this.accountChannel.bind('ContactUpdated', (data: string) => {
      const contact = this.createContact(data);

      if (contact.source === 'Bhive') {
        this.rootStore.personStore.loadPersonByIdGetSuccess(contact);
      }

      void this.rootStore.personStore.handleUpdateContactSuccess(contact);
    });

    this.accountChannel.bind('ContactDeleted', (data: string) => {
      const contact = this.createContact(data, true);

      if (contact.source === 'Bhive') {
        this.rootStore.personStore.removePersonById(contact.sourceId, true);
      }

      void this.rootStore.personStore.deleteContactFromListOfAllContactsWithNumber(
        contact
      );
    });

    this.personalChannel.bind('ContactCreated', (data: string) => {
      const contact = this.createContact(data);

      void this.rootStore.personStore.handleUpdateContactSuccess(contact);
    });

    this.personalChannel.bind('ContactUpdated', (data: string) => {
      const contact = this.createContact(data);

      void this.rootStore.personStore.handleUpdateContactSuccess(contact);
    });

    this.personalChannel.bind('ContactDeleted', (data: string) => {
      const contact = this.createContact(data);

      void this.rootStore.personStore.deleteContactFromListOfAllContactsWithNumber(
        contact
      );
    });

    /** -- Presence -- */
    this.accountChannel.bind('PresenceUpdated', (data: string) => {
      const presDto = JSON.parse(data) as IPresenceUpdated;
      const presUpdate = PresenceModel.FromPusherResponseDto(presDto);
      this.rootStore.uiStore.insertLocalPushPresence(presUpdate);
    });

    this.accountChannel.bind('StatusMessageUpdated', (data: string) => {
      const presDto = JSON.parse(data) as IMessageStatusModel;
      const messStatusUpdate =
        MessageStatusModel.FromPusherResponseDto(presDto);
      this.rootStore.uiStore.insertLocalPushStatusMessage(messStatusUpdate);
    });
    /** -- Conversation -- */

    this.personalChannel.bind('ConversationJoin', (data: string) => {
      const cjnDto = JSON.parse(data) as IConversationUpdatedNotification;
      if (!NODE_ENV_PRODUCTION) {
        console.debug('ConversationJoin', cjnDto);
      }
      // Eagerly initialize to guarantee the Unread Count shows up for newly joined Conversations
      this.rootStore.uiStore.conversationUnreadCounts.set(
        cjnDto.conversationId,
        { unreadMessages: 0, unreadMentions: 0 }
      );

      const previousConversationSnapshot =
        this.rootStore.conversationStore.selectConversationById(
          cjnDto.conversationId
        );

      // Reloads participants when user has rejoined a group
      previousConversationSnapshot?.then(({ data }) => {
        if (!data.isActiveParticipant) {
          this.rootStore.participantStore.fetchAndLoadConversationParticipants(
            cjnDto.conversationId
          );
        }
      });

      const convPbo =
        this.rootStore.conversationStore.loadConversationByIdIfMissingGet(
          cjnDto.conversationId
        );
      convPbo.then((conv) => {
        this.rootStore.uiStore.setConversationAndTotalUnreadCount(
          conv.data.id,
          conv.data.unreadCount,
          convPbo,
          conv.data.unreadMentionsCount
        );
        conv.data.setIsActiveParticipant(true);
      });

      useConversationStore
        .getState()
        .updateConversationFields(cjnDto.conversationId, {
          isActiveParticipant: true,
        });

      this.addParticipantsToConversation(
        cjnDto.conversationId,
        cjnDto.participants
      );
    });

    this.personalChannel.bind('ConversationUpdated', (data: string) => {
      const cunDto = JSON.parse(data) as IConversationUpdatedNotification;
      if (!NODE_ENV_PRODUCTION) {
        console.debug('ConversationUpdated', cunDto);
      }
      const conv = this.rootStore.conversationStore.selectConversationById(
        cunDto.conversationId
      );
      void conv?.then((c) => {
        if (cunDto.activeConference) {
          c.data.setActiveConference({
            id: cunDto.activeConference.id,
            sessionId: cunDto.activeConference.sessionId,
            provider: cunDto.activeConference.provider,
            start: cunDto.activeConference.start,
            adminId: cunDto.activeConference.adminId,
          });
        } else {
          c.data.setActiveConference(null);
        }
      });

      if (conv !== undefined && conv instanceof ConversationModel) {
        conv.setTopic(cunDto.topic);
        conv.setDescription(cunDto.description);
      } else {
        const isConversationArchived =
          !this.rootStore.conversationStore.conversationByIdRecentHist.has(
            cunDto.conversationId
          );
        void this.rootStore.conversationStore.loadConversationByIdGet(
          cunDto.conversationId,
          !isConversationArchived // if the conversation is archived, lets not reactivate it
        );
      }
    });

    this.personalChannel.bind('ConversationLeave', (data: string) => {
      const clnDto = JSON.parse(data) as IConversationNotification;
      if (!NODE_ENV_PRODUCTION) {
        console.debug('ConversationLeave', clnDto);
      }

      if (
        this.rootStore.conversationStore.deletingConversationIds.has(
          clnDto.conversationId
        ) ||
        this.rootStore.conversationStore.recentlyDeletedConversationIds.has(
          clnDto.conversationId
        )
      ) {
        return;
      }

      this.rootStore.conversationStore
        .selectConversationById(clnDto.conversationId)
        .then(
          ({ data }) => data.setIsActiveParticipant(false),
          (reason) =>
            useNotificationStore.getState().addNotify({
              type: 'error',
              dismissible: true,
              title: 'Error disabling conversation',
              message:
                "It wasn't possible to disable the conversation. Please refresh the page.",
            })
        );

      // Update conversations list
      useConversationStore
        .getState()
        .updateConversationFields(clnDto.conversationId, {
          isActiveParticipant: false,
        });

      this.rootStore.conversationStore
        .fetchConversationById(clnDto.conversationId)
        .then(({ lastMessage }) =>
          this.processReceivedMessage(
            { ...lastMessage, messageId: lastMessage.id },
            false,
            true
          )
        );

      this.removeParticipantsFromConversation(
        clnDto.conversationId,
        [
          {
            id: `${this.rootStore.personStore.loggedInPersonId}_`,
            personId: this.rootStore.personStore.loggedInPersonId,
          },
        ],
        false
      );
    });

    /** -- Message -- */
    this.personalChannel.bind('MessageCreated', async (data) => {
      const mcnDto = JSON.parse(data) as IMessageUpdatedNotification;

      // show sidebar for video call if type is not AdHocScheduled (video for later)
      if (
        mcnDto.personId !== this.rootStore.personStore.loggedInPersonId &&
        mcnDto.conference &&
        mcnDto.conference.type !== 'AdHocScheduled'
      ) {
        // Softphone Coordination: Block legacy video conference notifications when new softphone is active
        const coordinator = SoftphoneCoordinator.getInstance();
        const personId = this.rootStore.personStore.loggedInPersonId;
        const isNewSoftphoneEnabled = coordinator.readPreferenceSync(personId);

        if (isNewSoftphoneEnabled) {
          const callManager = CallManager.getInstance();

          callManager.initialize({
            person: this.rootStore.personStore,
            preference: this.rootStore.preferenceStore,
            ui: this.rootStore.uiStore,
            notification: this.rootStore.notificationStore,
            conversation: this.rootStore.conversationStore,
          });

          await callManager.handleVideoConferenceInvite({
            conference: mcnDto.conference,
            personId: mcnDto.personId,
            conversationId: mcnDto.conversationId,
          });
        } else {
          // Legacy video conference notification
          const presenceStatus =
            this.rootStore.uiStore.selectPersonPresenceStatus(
              this.rootStore.personStore.loggedInPersonId.toString()
            ).state;
          const showNotification = presenceStatus !== 'DoNotDisturb';
          const audioNotif = new Audio(videoStartNotificationMp3);
          await this.playSound(showNotification, audioNotif);
          this.rootStore.conversationStore.addVideoConferenceToList(
            mcnDto.conference,
            mcnDto.personId
          );
        }
      }

      const hasUserLeftConversation = !(await this.rootStore.conversationStore
        .selectConversationById(mcnDto.conversationId)
        ?.case({ fulfilled: (resp) => resp.data.isActiveParticipant }));

      return this.processReceivedMessage(
        mcnDto,
        false,
        hasUserLeftConversation
      );
    });

    /** -- Mentioned -- */
    this.personalChannel.bind('Mentioned', (data) => {
      const mcnDto = JSON.parse(data) as IMessageUpdatedNotification;
      if (!NODE_ENV_PRODUCTION) {
        const mcnRedacted = { ...mcnDto, chat: undefined, sms: undefined };
        console.debug('Mentioned', mcnRedacted);
      }

      return this.processReceivedMessage(mcnDto, true);
    });

    /** -- Read message -- */
    this.personalChannel.bind('ConversationViewStateUpdated', (data) => {
      const event = JSON.parse(data) as IConversationViewStateUpdated;
      const { conversationId, viewState } = event;

      if (!conversationId || !viewState) return;

      runInAction(() => {
        const conversationPbo =
          this.rootStore.conversationStore.conversationByIdMap.get(
            conversationId
          ) ?? null;
        // Update unread counts in uiStore and ConversationModel
        this.rootStore.uiStore.setConversationAndTotalUnreadCount(
          conversationId,
          viewState.unreadCount,
          conversationPbo,
          0
        );
        // Update lastReadMessageId if provided
        if (viewState.lastReadMessageId) {
          this.rootStore.participantStore.lastReadByConvMap.set(
            conversationId,
            viewState.lastReadMessageId
          );
          // Update ConversationModel readMessageId (not updated by setConversationAndTotalUnreadCount)
          if (conversationPbo?.state === 'fulfilled') {
            conversationPbo.value.data.readMessageId =
              viewState.lastReadMessageId;
          }
        }
      });
    });

    this.personalChannel.bind('CallLogCreated', async (data) => {
      const pusherEvent = JSON.parse(data) as CallLogType;
      if (pusherEvent) {
        await Promise.all([
          this.#queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_FETCH_CALL_LOGS],
          }),
          this.#queryClient.invalidateQueries({
            queryKey: BADGE_COUNTERS_QUERY_KEYS.missedCalls(),
            exact: false,
          }),
        ]);
      }
    });

    this.personalChannel.bind('person-history-updated', async (data) => {
      const pusherEvent = JSON.parse(data) as { callsLastView: number };

      if (pusherEvent?.callsLastView) {
        const callsLastView = getUnixTime(new Date(pusherEvent.callsLastView));

        this.#queryClient.setQueryData<PersonHistoryResponse>(
          BADGE_COUNTERS_QUERY_KEYS.personHistory,
          (existingHistory) => {
            if (!existingHistory) {
              return existingHistory;
            }
            return {
              ...existingHistory,
              callsLastView,
            };
          }
        );

        await this.#queryClient.invalidateQueries({
          queryKey: BADGE_COUNTERS_QUERY_KEYS.missedCalls(),
          exact: false,
        });
      }
    });

    this.personalChannel.bind('MessageUpdated', async (data) => {
      const munDto = JSON.parse(data) as IMessageUpdatedNotification;
      const utcNowStr = moment.utc().toISOString();
      const gifFromUpdate = normalizeGifPayload(munDto.gif);

      const localMsg = new MessageModel({
        id: munDto.messageId,
        created: utcNowStr,
        updated: null,
        personId: munDto.personId,
        phone: munDto.phone,
        chat: munDto.chat,
        gif: gifFromUpdate,
        documents: munDto.documents || [],
        sms: munDto.sms,
        call: munDto.call,
        conference: munDto.conference,
        systemEvent: munDto.systemEvent,
        isDeleted: munDto.isDeleted,
        isPush: true,
        references: munDto.references,
        reactions: munDto.reactions,
      });

      const reactionsUpdated = munDto?.updateEvents?.some(
        (el: { event: string }) =>
          el.event === 'Reaction.Added' || el.event === 'Reaction.Removed'
      );

      await runInAction(async () => {
        await this.rootStore.messageStore.editLocalMessage(
          munDto.conversationId,
          localMsg,
          munDto?.ancestorId,
          reactionsUpdated
        );
      });

      if (
        this.rootStore.conversationStore.selectConversationById(
          munDto.conversationId
        ) !== null
      ) {
        if (!NODE_ENV_PRODUCTION) {
          console.debug('MessageUpdated', munDto);
        }

        const pinnedMessages = this.rootStore.uiStore.listOfPinnedMessages.get(
          munDto.conversationId
        );

        const isPinned = pinnedMessages?.find(
          (item) => item.id === munDto.messageId
        );

        if (isPinned) {
          const updatedPinMessages = pinnedMessages.map((message) =>
            message.id !== munDto.messageId
              ? message
              : {
                  ...message,
                  chat: munDto.chat,
                  reactions: munDto.reactions,
                }
          );

          runInAction(() =>
            this.rootStore.uiStore.listOfPinnedMessages.set(
              munDto.conversationId,
              updatedPinMessages
            )
          );
        }

        const grpMsgs =
          this.rootStore.messageStore.groupedMessagesByConversationMap.get(
            munDto.conversationId
          );

        if (grpMsgs !== undefined) {
          const updatedMsg = grpMsgs.AllMessagesDescending.find(
            (m) => m.id === munDto.messageId
          );

          if (!isNullOrUndefined(updatedMsg)) {
            const conferenceStopped = munDto?.updateEvents?.some(
              (el: any) => el.event === 'Conference.Stopped'
            );

            if (conferenceStopped && munDto.conference) {
              updatedMsg.setConference({
                ...munDto.conference,
                hasRecording: munDto.conference.hasRecording ?? false,
              });

              this.rootStore.conversationStore.removeVideoConferenceFromList(
                munDto.conference.id
              );
            } else if (!isNullOrUndefined(updatedMsg.sms)) {
              updatedMsg.setSms(munDto.sms);
            } else if (!isNullOrUndefined(updatedMsg.documents)) {
              updatedMsg.setDocuments(munDto.documents);
            } else if (!isNullOrUndefined(updatedMsg.chat)) {
              updatedMsg.setChat(munDto.chat);
            }

            runInAction(() => {
              grpMsgs.updateMessage(updatedMsg);
            });
          } else {
            console.warn(
              `PusherStore MessageUpdated: Failed to find updatedMsg ${munDto.messageId}`
            );
          }
        }
      } else if (!NODE_ENV_PRODUCTION) {
        console.debug(
          `Ignoring MessageUpdated notification, ${munDto.conversationId} is not currently loaded.`
        );
      }
    });

    this.personalChannel.bind('MessageRemoved', (data) => {
      const munDto = JSON.parse(data) as IMessageUpdatedNotification;
      const redactedLocalMsg = { ...munDto, chat: undefined, sms: undefined };
      this.rootStore.messageStore.deleteLocalMessage(
        munDto.conversationId,
        munDto.messageId
      );
      // TODO: Find a way to ignore these notifications if they are the same `personId` AND the same device (how do we determine)? (RP 11/16/2017)
      if (munDto.personId !== this.rootStore.personStore.loggedInPersonId) {
        if (
          this.rootStore.conversationStore.selectConversationById(
            munDto.conversationId
          ) !== null
        ) {
          if (!NODE_ENV_PRODUCTION) {
            console.debug('MessageDeleted', redactedLocalMsg);
          }
          const grpMsgs =
            this.rootStore.messageStore.groupedMessagesByConversationMap.get(
              munDto.conversationId
            );
          if (grpMsgs !== undefined) {
            const updatedMsg = grpMsgs.AllMessagesDescending.find(
              (m) => m.id === munDto.messageId
            );
            if (!isNullOrUndefined(updatedMsg.sms)) {
              updatedMsg.setSms(munDto.sms);
            } else {
              updatedMsg.setChat(munDto.chat);
              updatedMsg.setDocuments([]);
              updatedMsg.setGif(undefined);
              const pinnedMessages =
                this.rootStore.uiStore.listOfPinnedMessages.get(
                  munDto.conversationId
                );
              const isPinned = pinnedMessages?.find(
                (item) => item.id === munDto.messageId
              );
              if (isPinned) {
                const filteredPinMess = pinnedMessages.filter(
                  (item) => item.id !== munDto.messageId
                );
                runInAction(() =>
                  this.rootStore.uiStore.listOfPinnedMessages.set(
                    munDto.conversationId,
                    filteredPinMess
                  )
                );
              }
            }
          }
        } else if (!NODE_ENV_PRODUCTION) {
          console.debug(
            `Ignoring MessageDeleted notification, ${munDto.conversationId} is not currently loaded.`
          );
        }
      } else if (!NODE_ENV_PRODUCTION) {
        console.debug(
          `Ignoring MessageDeleted notification, ${munDto.personId} is the logged-in user.`
        );
      }
    });

    /** -- Participant -- */
    this.personalChannel.bind('ParticipantsUpdated', (data: string) => {
      const punDto = JSON.parse(data) as IParticipantsUpdatedNotification;
      if (!NODE_ENV_PRODUCTION) {
        console.debug('ParticipantsUpdated', punDto);
      }
      const conv = this.rootStore.conversationStore.selectConversationById(
        punDto.conversationId
      );
      if (conv !== undefined && conv.state === 'fulfilled') {
        this.processUpdatedParticipants(punDto);
      }
    });

    /** -- Remove conversation from search list -- */
    this.personalChannel.bind('ConversationRemovedFromRank', (data: string) => {
      const punDto = JSON.parse(data);
      if (
        this.rootStore.conversationStore.conversationByIdMap.has(
          punDto?.conversationId
        ) ||
        this.rootStore.conversationStore.conversationByIdRecentHist.has(
          punDto?.conversationId
        )
      ) {
        if (
          this.rootStore.conversationStore.FavoriteConversationIds.includes(
            punDto?.conversationId
          )
        ) {
          this.rootStore.conversationStore.removeConversationFromFavoritesPatch(
            punDto?.conversationId
          );
        }
        this.rootStore.conversationStore.removeLocalConversationFromList(
          punDto?.conversationId
        );
        if (!NODE_ENV_PRODUCTION) {
          console.debug('ConversationRemovedFromRank', punDto);
        }
      }
    });

    /** -- Pinned Messages -- */
    this.personalChannel.bind('MessagePinned', (data: string) => {
      const punDto = JSON.parse(data) as IPinnedMessageNotification;
      if (!NODE_ENV_PRODUCTION) {
        console.debug('Pinned message created', punDto);
      }
      let callText = punDto.call ? '' : null;
      const callPreText = punDto.call
        ? punDto.call?.direction === 'Incoming'
          ? 'Call from:'
          : 'Called'
        : null;
      if (punDto.call?.to.personId) {
        const personPbo = this.rootStore.personStore.selectPersonById(
          punDto.call.to.personId
        );
        personPbo.case({
          fulfilled: (resp) => (callText = resp.name),
        });
      } else if (punDto.call?.to.phone) {
        callText = punDto.call.to.phone;
      }
      const pinnedMessage: IPinnedMessages = {
        pinnedByPersonId: punDto.pinnedByPersonId,
        pinnedAt: punDto.pinnedAt,
        id: punDto.messageId,
        created: punDto.created,
        personId: punDto.personId,
        chat: {
          text: punDto.chat?.text,
          text_v2: punDto.chat?.text_v2,
        },
        documents: punDto.documents,
        conference: {
          displayName:
            punDto.conference?.displayName &&
            `Conference: ${punDto.conference?.displayName}`,
        },
        call: {
          text: callText && `${callPreText} ${callText}`,
        },
        references: punDto.references,
        systemEvent: { eventType: punDto.systemEvent?.eventType },
        conversationId: punDto.conversationId,
        source: punDto.source,
        sms: punDto.sms,
        gif: punDto.gif,
        reactions: (punDto as any).reactions,
      };
      const pinnedMessages =
        this.rootStore.uiStore.listOfPinnedMessages.get(
          punDto.conversationId
        ) || [];
      runInAction(() => {
        const newPinnedMessageTime = new Date(pinnedMessage.created).getTime();

        const indexToInsert = pinnedMessages.findIndex(
          (message) =>
            new Date(message.created).getTime() > newPinnedMessageTime
        );

        indexToInsert > -1
          ? pinnedMessages.splice(indexToInsert, 0, pinnedMessage)
          : pinnedMessages.push(pinnedMessage);

        this.rootStore.uiStore.listOfPinnedMessages.set(
          punDto.conversationId,
          pinnedMessages
        );
      });
      document.getElementById('context-panel')?.click();
    });

    this.personalChannel.bind('MessageUnpinned', (data: string) => {
      const punDto = JSON.parse(data) as IPinnedMessageNotification;
      if (!NODE_ENV_PRODUCTION) {
        console.debug('Pinned message deleted', punDto);
      }
      const messages = this.rootStore.uiStore.listOfPinnedMessages.get(
        punDto.conversationId
      );
      const newData = messages.filter(
        (message) => message.id !== punDto.messageId
      );
      runInAction(() =>
        this.rootStore.uiStore.listOfPinnedMessages.set(
          punDto.conversationId,
          newData
        )
      );
      document.getElementById('context-panel')?.click();
    });

    /** -- Shared Files & Links -- */
    this.personalChannel.bind('SharedItemCreated', (data: string) => {
      const payload = JSON.parse(data) as ISharedItemCreatedNotification;
      if (!payload?.conversationId || !payload?.itemId) {
        return;
      }

      if (!NODE_ENV_PRODUCTION) {
        console.debug('SharedItemCreated', payload);
      }

      upsertSharedItemInQueryCache(
        this.#queryClient,
        payload.conversationId,
        mapSharedItemCreatedToSharedItem(payload)
      );
    });

    this.personalChannel.bind('SharedItemRemoved', (data: string) => {
      const payload = JSON.parse(data) as ISharedItemRemovedNotification;
      if (!payload?.conversationId || !payload?.itemId) {
        return;
      }

      if (!NODE_ENV_PRODUCTION) {
        console.debug('SharedItemRemoved', payload);
      }

      removeSharedItemFromQueryCache(
        this.#queryClient,
        payload.conversationId,
        payload.itemId
      );
    });

    this.personalChannel.bind('PreferencesUpdated', async (data: string) => {
      const punDto = JSON.parse(data) as IFavoritesMutedMentionNotification;
      const { muteConversationLocally } = this.rootStore.uiStore;
      const preferenceProperties = [
        'notificationAudio',
        'directMentionsOnly',
        'showCallMessagesInChat',
        'floatingSoftphone',
      ];
      if (punDto.mutedConversationIds) {
        muteConversationLocally(punDto.mutedConversationIds);
      } else if (punDto.favoriteConversationIds) {
        this.rootStore.conversationStore.loadFavoriteConversationsGet();
      } else if (
        preferenceProperties.some((prop) => punDto.hasOwnProperty(prop))
      ) {
        this.rootStore.preferenceStore.clearAllData(true);
        await this.rootStore.preferenceStore.getExistingPreferenceData();
      }
    });

    /** -- 10DLC -- */
    this.accountChannel.bind('TcrOptStatusUpdated', (messageJson: string) => {
      try {
        const { tcrOptOut, number } = JSON.parse(messageJson) as {
          tcrOptOut: boolean;
          number: string;
        };

        if (number) {
          useConversationStore
            .getState()
            .updateOptOutPhoneNumberInfo(number, tcrOptOut);
        }
      } catch (error) {
        console.error(
          `Message received in TcrOptStatusUpdated pusher message is not a valid JSON`
        );
      }
    });

    /** -- Voicemails -- */
    this.personalChannel.bind(
      'person-voicemail-created',
      (eventData: string) => {
        void this.#queryClient.setQueryData<VoicemailBadgeCounter>(
          BADGE_COUNTERS_QUERY_KEYS.voicemails,
          (prevState) => {
            if (prevState) {
              return {
                ...prevState,
                total: Math.max(prevState.total + 1, 0),
              };
            }
            return prevState;
          }
        );
      }
    );

    this.personalChannel.bind('person-voicemail-read', (eventData: string) => {
      const id = JSON.parse(eventData).id;

      if (this.lastVoicemailId === id) {
        return;
      }
      this.setLastVoicemailId(id as number);

      void this.#queryClient.setQueryData<VoicemailBadgeCounter>(
        BADGE_COUNTERS_QUERY_KEYS.voicemails,
        (prevState) => {
          if (prevState) {
            return {
              ...prevState,
              total: Math.max(prevState.total - 1, 0),
            };
          }
          return prevState;
        }
      );
    });

    this.personalChannel.bind(
      'person-new-voicemail-deleted',
      (eventData: string) => {
        void this.#queryClient.setQueryData<VoicemailBadgeCounter>(
          BADGE_COUNTERS_QUERY_KEYS.voicemails,
          (prevState) => {
            if (prevState) {
              return {
                ...prevState,
                total: Math.max(prevState.total - 1, 0),
              };
            }
            return prevState;
          }
        );
      }
    );
  };

  closeConnection = () => {
    this.socket.disconnect();
    this.setOnline(false);
  };

  openConnection = () => {
    const ps = this.rootStore.personStore;
    const cs = this.rootStore.configStore;

    const signedInPersonConfig = cs.signedInPersonConfig;
    const isLoggedIn = Boolean(ps?.IsLoggedIn);
    const isSignedInPersonConfigFulfilled =
      signedInPersonConfig?.state === 'fulfilled';
    const signedInPersonConfigData =
      signedInPersonConfig?.state === 'fulfilled'
        ? signedInPersonConfig.value.data
        : null;

    if (
      !isLoggedIn ||
      !isSignedInPersonConfigFulfilled ||
      signedInPersonConfigData === null
    ) {
      return;
    }

    if (
      this.isSocketConnected &&
      this.isPersonalChannelActuallySubscribed &&
      this.isAccountChannelActuallySubscribed
    ) {
      return;
    }

    this.clearAllData();
    this.initSubscriptions(signedInPersonConfigData);
  };

  createContact = (data, isDeleted = false) => {
    const parsedPusherContact = JSON.parse(data) as IPusherContact;

    return ContactModelV2.fromPusherContact({
      ...parsedPusherContact,
      deleted: isDeleted,
    });
  };

  /**
   * Adds participants to a conversation
   * @param conversationId - The ID of the conversation to add participants to
   * @param participants - The participants to add
   */
  addParticipantsToConversation = (
    conversationId: string,
    participants: ParticipantSummary[]
  ) => {
    this.rootStore.participantStore.insertLocalPushParticipants(
      conversationId,
      participants
    );
  };

  /**
   * Removes participants from a conversation
   * @param conversationId - The ID of the conversation to remove participants from
   * @param participants - The participants to remove
   * @param shouldFetchConversation - Whether to fetch the conversation after removing participants
   */
  removeParticipantsFromConversation = (
    conversationId: string,
    participants: ParticipantSummary[],
    shouldFetchConversation = true
  ) => {
    if (!isEmpty(participants)) {
      this.rootStore.participantStore.removeLocalParticipants(
        conversationId,
        participants
      );

      if (shouldFetchConversation) {
        void this.#queryClient.fetchQuery({
          queryKey: [QUERY_KEY_FETCH_CONVERSATION],
          queryFn: async () =>
            fetchConversation(this.rootStore, conversationId),
          staleTime: 0,
        });
      }
    }
  };

  /**
   * Processes added and removed participants in a conversation.
   * Filters out the logged-in user from removed so we do not call remove when
   * the event is only about the current user (ConversationLeave handles that).
   * @param punDto - The participants updated notification.
   */
  processUpdatedParticipants = (punDto: IParticipantsUpdatedNotification) => {
    if (!isEmpty(punDto.added)) {
      this.addParticipantsToConversation(punDto.conversationId, punDto.added);
    }
    if (!isEmpty(punDto.removed)) {
      const loggedInPersonId = this.rootStore.personStore.loggedInPersonId;
      const removedExcludingSelf = punDto.removed.filter(
        (p) => p.personId !== loggedInPersonId
      );

      // Ensure removed participants are resolved before the system message renders.
      removedExcludingSelf.forEach((p) => {
        if (p.personId > 0) {
          this.rootStore.personStore.loadPersonByIdGetIfMissingGet(
            p.personId.toString()
          );
        }
      });

      if (!isEmpty(removedExcludingSelf)) {
        this.removeParticipantsFromConversation(
          punDto.conversationId,
          removedExcludingSelf
        );
      }
    }
  };

  private socket: Pusher = null;
  private personalChannel: Channel = null;
  private presenceChannel: Channel = null;
  private accountChannel: Channel = null;

  @computed
  get isPersonalChannelActuallySubscribed(): boolean {
    return (
      this.isPersonalChannelSubscribed ||
      (this.personalChannel !== null &&
        this.personalChannel.subscribed === true)
    );
  }

  @computed
  get isAccountChannelActuallySubscribed(): boolean {
    return (
      this.isAccountChannelSubscribed ||
      (this.accountChannel !== null && this.accountChannel.subscribed === true)
    );
  }

  playSound = async (
    showNotification: boolean,
    audioNotif: HTMLMediaElement
  ) => {
    if (showNotification) {
      const sp: string = await localforage.getItem<string>(
        SELECTED_RING_DEVICE
      );
      try {
        // @ts-ignore
        await audioNotif.setSinkId(sp || 'default');
        console.debug(
          `Success, Sound Notification output device attached to element with ${audioNotif.title} as source.`
        );
      } catch (error) {
        console.debug(
          `Failed, Sound Notification output device attached to element with ${audioNotif.title} as source.`
        );
      }

      try {
        await audioNotif.play();
      } catch (error) {
        console.warn('Unable to play incoming tone.', error);
        /* Not allowed error is thrown when play() is considered an autoplay
          and the browser is blocking that until the user interacts with the page.
          Not supported error is thrown when audio formarts are not supported.
          Since we only use .mp3 this error will only happen in certain scenarios that we cannot control.
          Both This errors will be ignored
        */
        if (
          error.name !== 'NotAllowedError' &&
          error.name !== 'NotSupportedError'
        ) {
          bugsnagClient.notify(error, (event) => {
            event.severity = 'error';
            event.context = 'incomingCall';
          });
        }
      }
    }
  };

  @action
  ifNotRecentAdd = (
    conversationId: string,
    convResp: AxiosResponseT<ConversationModel>
  ) => {
    if (
      !this.rootStore.conversationStore.conversationByIdRecentHist.has(
        conversationId
      )
    ) {
      useConversationStore.getState().addConversationToList(convResp.data);
    }
  };

  private getMessageBodyFromDto = (mcnDto: IMessageUpdatedNotification) => {
    if (normalizeGifPayload(mcnDto.gif)) {
      return 'GIF';
    }

    const chatText = get(mcnDto, 'chat.text');
    const smsText = get(mcnDto, 'sms.text');
    const attachments = get(mcnDto, 'documents');

    if (!chatText && !smsText && !attachments?.length) {
      return '';
    }

    return (
      chatText ||
      smsText ||
      (attachments.length === 1 ? 'shared a file' : 'shared files')
    );
  };

  private replacePersonMentionsInMessage(message: string): string {
    return message.replace(/@pr(\d+)/g, (match, personIdStr) => {
      const personId = parseInt(personIdStr);
      if (!Number.isInteger(personId)) {
        return match;
      }

      const person = this.rootStore.personStore.selectPersonValueById(personId);
      if (!person?.name) {
        return match;
      }

      return person.name;
    });
  }

  /**
   * Resolves the notification title for a "OneOnOne" `Conversation`.
   *
   * The sender of a received message in a 1:1 is, by definition, the other
   * party, so no `Participant` lookup is needed. Never falls back to `topic`:
   * for a "OneOnOne" that is the internal identifier "OneOnOne--<id>"
   * (CUU2-3526).
   */
  private resolveOneOnOneNotificationTitle = (
    mcnDto: IMessageUpdatedNotification,
    sender: ContactModelV2 | null
  ): string => {
    const senderName = formatPersonFullName(sender);
    if (senderName) {
      return senderName;
    }

    if (mcnDto.phone) {
      const contact = this.rootStore.personStore
        .getAllContactsWithSameNum(mcnDto.phone)
        ?.case({
          fulfilled: (contacts) => {
            return contacts[0];
          },
        });

      return contact?.name || mcnDto.phone;
    }

    return ONE_ON_ONE_FALLBACK_TITLE;
  };

  private processReceivedMessage(
    mcnDto: IMessageUpdatedNotification,
    isMention: boolean,
    isLeavingConversation?: boolean
  ) {
    if (
      this.rootStore.conversationStore.deletingConversationIds.has(
        mcnDto.conversationId
      )
    ) {
      return;
    }

    const isSystemMessage =
      !isEmpty(mcnDto.systemEvent) ||
      !isEmpty(mcnDto.conference) ||
      !isEmpty(mcnDto.call);

    const gifFromPush = normalizeGifPayload(mcnDto.gif);

    const localMsg = new MessageModel({
      id: mcnDto.messageId,
      created:
        mcnDto.created ||
        getISOStringFromTimeUUID(mcnDto.messageId) ||
        moment.utc().toISOString(),
      updated: null,
      personId: mcnDto.personId,
      phone: mcnDto.phone || null,
      chat: mcnDto.chat,
      gif: gifFromPush,
      documents: mcnDto.documents,
      sms: mcnDto.sms,
      call: mcnDto.call,
      conference: mcnDto.conference,
      systemEvent: mcnDto.systemEvent,
      isDeleted: mcnDto.isDeleted,
      isPush: true,
      references: mcnDto.references,
      reactions: mcnDto.reactions,
    });

    let convPromise = this.rootStore.conversationStore.selectConversationById(
      mcnDto.conversationId
    ) as PromiseLike<AxiosResponseT<ConversationModel>>;

    if (convPromise === null) {
      convPromise =
        this.rootStore.conversationStore.loadConversationByIdIfMissingGet(
          mcnDto.conversationId,
          false, // we don't want to reactivate archived conversations just yet; only if this is NOT a system event/message
          true
        );
    }

    void convPromise.then(async (conv) => {
      conv.data.setLastMessageDate(localMsg.created);
      conv.data.setLastMessageId(localMsg.id);

      const currentConversationId = getCurrentConversationId();

      // In the current Conversation, and it is Focused or has recent Focus/Keyboard/Mouse Activity
      const shouldClearUnreads =
        currentConversationId === mcnDto.conversationId &&
        this.rootStore.uiStore.IsFocused &&
        this.rootStore.uiStore.selectHasRecentActivityWithin(
          PRESENCE_IDLE_TIME
        );
      console.debug('isFromCurrConvWithRecentActivity', shouldClearUnreads);
      const isOwnMessage =
        mcnDto.personId === this.rootStore.personStore.loggedInPersonId;
      const messageMethodOrCount =
        shouldClearUnreads || isOwnMessage ? 0 : 'add';
      const mentionMethodOrCount =
        shouldClearUnreads || isOwnMessage
          ? 0
          : isMention
            ? 'add'
            : null;
      const activeConference =
        this.rootStore.conversationStore &&
        this.rootStore.uiStore.IsOnVideoConference;

      let errorFetchingFullMessage = false;
      if (mcnDto.hasMore) {
        try {
          const fullMessage = await fetchMessage(
            mcnDto.conversationId,
            mcnDto.messageId
          );

          if (fullMessage) {
            localMsg.chat = fullMessage.chat;
            mcnDto.chat = fullMessage.chat;
            const gifFromFullMessage = normalizeGifPayload(fullMessage.gif);
            if (gifFromFullMessage) {
              localMsg.setGif(gifFromFullMessage);
            }
          }
        } catch (e) {
          errorFetchingFullMessage = true;
        }
      }

      // The latest `Message` Id (used withing `updateMyLastReadMessage`) is a computed value that will be re-calculated due to the `insertNewLocalOrPushMessage` above.
      void this.rootStore.messageStore
        .insertNewLocalOrPushMessage(mcnDto.conversationId, localMsg)
        .then(async () => {
          if (!shouldClearUnreads) {
            console.warn(
              `(${mcnDto.conversationId} || Message Id ${localMsg.id}) updateMyLastReadMessage was not sent because it is not a current conversation with recent activity `
            );
            return Promise.resolve(null);
          }
          if (!isLeavingConversation) {
            try {
              // Only mark as read if the message is from another user, because we're already marking our own messages as read on MessageStore.createMessagePost
              if (
                localMsg.personId !==
                this.rootStore.personStore.loggedInPersonId
              ) {
                return await this.rootStore.participantStore.updateMyLastReadMessage(
                  mcnDto.conversationId,
                  localMsg.id
                );
              }
            } catch (reason) {
              console.error(
                `processReceivedMessage: Failed to updateMyLastReadMessage: ${reason}`
              );
            }
          }
        })
        .then(async () => {
          const { directMentionsOnly } =
            this.#queryClient.getQueryData<IPreferences>(['preferences']);

          if (!isSystemMessage) {
            // Dont update unread count for system messages/events CUU2-2500
            await this.rootStore.uiStore.setConversationAndTotalUnreadCount(
              mcnDto.conversationId,
              messageMethodOrCount,
              convPromise,
              mentionMethodOrCount
            );

            const isOneOnOne = conv.data?.grouping === 'OneOnOne';
            const willAdd =
              !isSystemMessage &&
              (!!conv.data?.isActiveParticipant || isOneOnOne);

            if (willAdd) {
              const conversationFieldUpdates: Partial<ConversationModel> = {};

              if (!conv.data?.isActiveParticipant) {
                conv.data?.setIsActiveParticipant(true);
                conversationFieldUpdates.isActiveParticipant = true;
              }

              if (conv.data?.archived) {
                conv.data.archived = false;
                conversationFieldUpdates.archived = false;
              }

              if (Object.keys(conversationFieldUpdates).length > 0) {
                useConversationStore
                  .getState()
                  .updateConversationFields(
                    mcnDto.conversationId,
                    conversationFieldUpdates
                  );
              }

              useConversationStore.getState().addConversationToList(conv.data);
            }
          }

          if (!isEmpty(mcnDto.systemEvent)) return;

          this.rootStore.messageStore.setNewestMessageOwnedByUserId(
            mcnDto.conversationId
          );
          // Sends if within Electron renderer.
          // Skipped for our own messages: flashing the taskbar for a message the
          // user just sent from another session is the same false "new activity"
          // signal as the unread badge (CUU2-3490).
          if (!isOwnMessage) {
            sendIpcNewMessage();
          }
          // If you are on the current conversation, we don't want to hit the DB to reload it
          const curConversation = getCurrentConversationId();
          // Otherwise, guarantee it is loaded (ex. it is new since you initially logged in)
          if (curConversation !== mcnDto.conversationId) {
            void this.rootStore.conversationStore.loadConversationByIdIfMissingGet(
              mcnDto.conversationId
            );
          }

          const mutedConversation = this.rootStore.uiStore.selectIfConvMuted(
            mcnDto.conversationId
          );

          if (mutedConversation || isOwnMessage) {
            return;
          }

          const isSMSOrShowAllMentions =
            !directMentionsOnly ||
            conv.data.participants.some(({ phone }) => phone);

          const presenceStatus =
            this.rootStore.uiStore.selectPersonPresenceStatus(
              this.rootStore.personStore.loggedInPersonId.toString()
            ).state;
          const showNotification = presenceStatus !== 'DoNotDisturb';
          const { topic, grouping } = conv.data;

          // Web Notifications
          if (!isOwnMessage) {
            // Awaiting the PBO covers both a cold cache (fetches the sender)
            // and a `Person` still pending from an earlier request, instead
            // of degrading the title (CUU2-3526). SMS senders come with
            // `personId: 0`; loading it would cache a "0" fallback `Person`
            // and show that as the title instead of the phone number.
            let otherPerson: ContactModelV2 | null = null;
            if (mcnDto.personId) {
              try {
                otherPerson =
                  (await this.rootStore.personStore.loadPersonByIdGetIfMissingGet(
                    mcnDto.personId.toString()
                  )) ?? null;
              } catch (reason) {
                console.error(
                  `processReceivedMessage: failed to load Person ${mcnDto.personId}`,
                  reason
                );
              }
            }

            // Builds the displayName
            let displayName = topic;
            const senderName =
              formatPersonFullName(otherPerson) || mcnDto.phone || null;

            if (grouping === 'OneOnOne') {
              displayName = this.resolveOneOnOneNotificationTitle(
                mcnDto,
                otherPerson
              );
            } else if (grouping === 'Group' && topic?.startsWith('Group--')) {
              this.rootStore.conversationStore.addProperName(conv.data);
              displayName = senderName
                ? `${conv.data.topic} | ${senderName}`
                : conv.data.topic;
            } else {
              displayName = senderName ? `${topic} | ${senderName}` : topic;
            }

            // Builds the message body description
            const currentMessageBodyDescription = this.getMessageBodyFromDto(
              mcnDto
            ) as string;

            // Replaces the person mentions with the DisplayName
            const messageBodyDescription: string =
              this.replacePersonMentionsInMessage(
                currentMessageBodyDescription
              );

            // Close existing MessageCreated notifications for this Conversation
            let hasExistingWebNotif = false;
            const hasConversationPreviouslyNotified =
              this.rootStore.notificationStore.messageCreatedWebNotifications.has(
                mcnDto.conversationId
              );

            if (hasConversationPreviouslyNotified) {
              hasExistingWebNotif =
                this.rootStore.notificationStore.messageCreatedWebNotifications.get(
                  mcnDto.conversationId
                ).length > 0;

              if (hasExistingWebNotif) {
                this.rootStore.notificationStore.closeAllMessageCreatedWebNotifications(
                  mcnDto.conversationId
                );
              }
            }

            // Builds the notification title and message
            let titleMessage = '';
            let message = '';
            let truncateMessage = false;
            let isMessageOrMention = false;

            if (
              isSMSOrShowAllMentions &&
              mcnDto.conference &&
              mcnDto.conference.adminId !==
                this.rootStore.personStore.loggedInPersonId
            ) {
              // Softphone Coordination: Skip legacy video conference notification if new softphone is active
              const coordinator = SoftphoneCoordinator.getInstance();
              const personId = this.rootStore.personStore.loggedInPersonId;
              const isNewSoftphoneEnabled =
                coordinator.readPreferenceSync(personId);

              if (!isNewSoftphoneEnabled) {
                // Legacy video conference notification
                titleMessage = 'New video conference';
                message = `${displayName}: Would like you to join a video conference call?`;
              }
              // If new softphone is active, skip this notification entirely
              // (the new softphone already handled it in the MessageCreated handler above)
            } else if (
              (isSMSOrShowAllMentions && isMention) ||
              (!isSMSOrShowAllMentions &&
                isMention &&
                grouping !== 'OneOnOne') ||
              isSMSOrShowAllMentions ||
              grouping === 'OneOnOne'
            ) {
              titleMessage = displayName;
              message = messageBodyDescription;
              truncateMessage = true;
              isMessageOrMention = true;
            } else {
              return;
            }

            // Softphone Coordination: Skip notification if video conference and new softphone is active
            const coordinator = SoftphoneCoordinator.getInstance();
            const personId = this.rootStore.personStore.loggedInPersonId;
            const isNewSoftphoneEnabled =
              coordinator.readPreferenceSync(personId);
            const isVideoConference =
              mcnDto.conference && mcnDto.conference.adminId !== personId;
            const shouldSkipNotification =
              isNewSoftphoneEnabled && isVideoConference;

            showNotification &&
              !mcnDto.call &&
              mcnDto?.conference?.type !== 'AdHocScheduled' &&
              !shouldSkipNotification &&
              this.rootStore.notificationStore.addWebNotification(
                titleMessage,
                {
                  tag: `${WN_MESSAGE_CREATED_PREFIX}${mcnDto.conversationId}:${mcnDto.messageId}`,
                  requireInteraction: false,
                  icon: otherPerson?.avatarUrl
                    ? otherPerson.DisplayAvatar
                    : 'https://www.gravatar.com/avatar/?d=identicon&s=128',
                  silent: true,
                  timeout: 30000,
                  body: hasExistingWebNotif ? `${message} ` : ` ${message} `,
                  onClick: () => {
                    if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
                      console.debug('mcn onClick for ' + mcnDto.conversationId);
                    }
                    if (
                      this.rootStore.conversationStore.conversationByIdRecentHist.has(
                        mcnDto.conversationId
                      )
                    ) {
                      this.rootStore.uiStore.setMessageIdToScroll(
                        mcnDto.messageId
                      );
                      this.rootStore.routerStore.push(
                        `/chat/conversations/${mcnDto.conversationId}/menu`
                      );
                    }
                  },
                  buttonLabel: isMessageOrMention ? 'Open' : undefined,
                },
                'info',
                false,
                truncateMessage
              );

            if (errorFetchingFullMessage) {
              this.rootStore.notificationStore.addNotification(
                'We had an issue loading the message, and some content might be missing.',
                'Error loading message',
                'error',
                'tr'
              );
            }
          }

          // Play Notification Audio
          if (this.rootStore.preferenceStore.preferences.notificationAudio) {
            let audioNotif: HTMLAudioElement;
            if (isSMSOrShowAllMentions && !activeConference) {
              // Play sound
              // Softphone Coordination: Skip video conference audio if new softphone is active
              const coordinator = SoftphoneCoordinator.getInstance();
              const personId = this.rootStore.personStore.loggedInPersonId;
              const isNewSoftphoneEnabled =
                coordinator.readPreferenceSync(personId);

              // Handle video conference events with coordination
              if (
                mcnDto.systemEvent?.eventType ===
                'Conference.Participant.Joined'
              ) {
                if (isNewSoftphoneEnabled) {
                  // Delegate to new softphone
                  const { CallManager } = await import(
                    'Modules/softphone/services/CallManager'
                  );
                  const callManager = CallManager.getInstance();
                  await callManager.handleVideoParticipantJoined();
                  return; // Skip legacy audio
                } else {
                  // Legacy: Play audio directly
                  audioNotif = new Audio(videoJoinNotificationMp3);
                }
              } else if (
                mcnDto.systemEvent?.eventType === 'Conference.Participant.Left'
              ) {
                if (isNewSoftphoneEnabled) {
                  // Delegate to new softphone
                  const { CallManager } = await import(
                    'Modules/softphone/services/CallManager'
                  );
                  const callManager = CallManager.getInstance();
                  await callManager.handleVideoParticipantLeft();
                  return; // Skip legacy audio
                } else {
                  // Legacy: Play audio directly
                  audioNotif = new Audio(videoLeaveNotificationMp3);
                }
              } else if (mcnDto.systemEvent?.eventType === 'Conference.Ended') {
                if (isNewSoftphoneEnabled) {
                  // Delegate to new softphone
                  const { CallManager } = await import(
                    'Modules/softphone/services/CallManager'
                  );
                  const callManager = CallManager.getInstance();
                  await callManager.handleVideoConferenceEnded();
                  return; // Skip legacy audio
                } else {
                  // Legacy: Play audio directly
                  audioNotif = new Audio(videoEndedNotificationMp3);
                }
              } else if (mcnDto.conference && !isNewSoftphoneEnabled) {
                // Legacy: Only play video start audio if new softphone is NOT active
                audioNotif = new Audio(videoStartNotificationMp3);
              } else {
                audioNotif = new Audio(chatNotificationMp3);
              }
              await this.playSound(showNotification, audioNotif);
            } else if (isMention && directMentionsOnly) {
              audioNotif = new Audio(chatNotificationMp3);
              showNotification &&
                localforage.getItem<string>(SELECTED_SPEAKER).then((sp) => {
                  try {
                    // @ts-ignore
                    audioNotif.setSinkId(sp || 'default').then(() => {
                      audioNotif.play();
                      console.debug(
                        `Success, Sound Notification output device attached to element with ${audioNotif.title} as source.`
                      );
                    });
                  } catch (error) {
                    audioNotif.play();
                    console.debug(
                      `Failed, Sound Notification output device attached to element with ${audioNotif.title} as source.`
                    );
                  }
                });
            } else if (grouping === 'OneOnOne' && directMentionsOnly) {
              audioNotif = new Audio(chatNotificationMp3);
              showNotification &&
                localforage.getItem<string>(SELECTED_SPEAKER).then((sp) => {
                  try {
                    // @ts-ignore
                    audioNotif.setSinkId(sp || 'default').then(() => {
                      audioNotif.play();
                      console.debug(
                        `Success, Sound Notification output device attached to element with ${audioNotif.title} as source.`
                      );
                    });
                  } catch (error) {
                    void audioNotif.play();
                    console.debug(
                      `Failed, Sound Notification output device attached to element with ${audioNotif.title} as source.`
                    );
                  }
                });
            }
          }
        });
    });
  }

  mobxHook = reaction(
    () =>
      [
        this.rootStore.personStore?.IsLoggedIn === true,
        this.rootStore.configStore?.signedInPersonConfig?.state === 'fulfilled',
      ] as const,
    ([isLoggedIn, isSignedInPersonConfigLoaded]) => {
      if (isLoggedIn && isSignedInPersonConfigLoaded) {
        this.openConnection();
      }
    },
    { fireImmediately: true, name: 'PusherStore.openConnectionReaction' }
  );
}
